(() => {
  'use strict';
  if (window.CgauEditorialEntrance) return;

  const KEY = 'cgau:editorial-entrance:v1';
  const script = document.currentScript;
  const pageType = (script?.dataset.pageType || '').toLowerCase();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const routes = Object.freeze({
    night: '/blogs/guides/clearer-security-camera-footage-at-night',
    cinema: '/pages/coming-soon',
    parcel: '/pages/condition-grading-guide',
    reach: '/pages/why-camguard-australia',
    express: '/policies/shipping-policy',
    returns: '/policies/refund-policy',
    warranty: '/pages/camguard-australia-warranty-policy',
    catalog: '/collections/all',
    range: '/collections/all',
    stock: '/collections/all-items',
    townsville: '/pages/about-us',
    nightlab: '/blogs/guides/the-night-lab',
    hub: '/collections/base-stations-hubs',
    doorbell: '/collections/smart-doorbells',
    lock: '/collections/smart-locks',
    kit: '/collections/full-kits',
    outdoor: '/collections/outdoor-cameras',
    indoor: '/collections/indoor-cameras',
    baby: '/collections/baby-monitors',
    sensor: '/collections/alarms-and-sensors',
    storage: '/collections/memory-storage',
    accessory: '/collections/accessories',
    renewed: '/collections/refurbished-used',
    latest: '/collections/new-releases'
  });
  const WORLD_SCENES = Object.freeze(['nightlab']);
  const KEYNOTE_SCENES = Object.freeze(['hub', 'doorbell', 'lock', 'kit', 'outdoor', 'indoor', 'baby', 'sensor', 'storage', 'accessory', 'renewed', 'latest']);
  // Scenes whose reveal must not be replayed by a native view transition.
  const GUARDED = Object.freeze(['catalog', 'stock', 'range', 'returns', 'express', 'warranty', 'townsville', 'reach', ...WORLD_SCENES, ...KEYNOTE_SCENES]);
  const heroTrustLabels = Object.freeze({
    express: 'free express post on $99+ orders',
    returns: '30-day returns',
    warranty: '12-month warranty'
  });
  const canonical = value => value.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/';
  const kindForPath = value => {
    const path = canonical(value);
    return Object.keys(routes).find(kind => routes[kind] === path) || '';
  };
  // Cart, checkout, account and the other protected surfaces are never an entrance source.
  const protectedSource = value =>
    /^\/(?:cart|checkout|account|search|policies|password|challenge)(?:\/|$)/.test(canonical(value));
  const blockedSource = value =>
    /(?:^|\/)products(?:\/|$)/.test(canonical(value)) || protectedSource(value);
  // The Townsville welcome is the only scene a product page may launch.
  const blockedSourceFor = (kind, value) =>
    kind === 'townsville' ? protectedSource(value) : blockedSource(value);
  const blockedTemplateFor = kind => kind === 'townsville'
    ? /^(?:cart|search|policy|404|password|customers\/login)$/.test(pageType)
    : /^(?:product|cart|search|policy|404|password|customers\/login)$/.test(pageType);
  const cinemaPaused = () => {
    try { return sessionStorage.getItem('cg-coming-motion-paused') === 'true'; }
    catch (_) { return false; }
  };

  let nativeEntranceIntent = '';
  let scene = null;
  let timer = 0;
  let frame = 0;
  let controller = null;
  const previousInert = new Map();

  const setState = (pending, running) => {
    window.CgauEditorialEntrancePending = pending;
    window.CgauEditorialEntranceRunning = running;
  };

  const restorePage = () => {
    document.documentElement.classList.remove('cgau-editorial-running');
    previousInert.forEach((value, node) => {
      if (node.isConnected) node.inert = value;
    });
    previousInert.clear();
  };

  const finish = (reason = 'complete') => {
    cancelAnimationFrame(frame);
    clearTimeout(timer);
    controller?.abort();
    controller = null;
    const activeScene = scene;
    const restoreFocus = Boolean(activeScene?.contains(document.activeElement)) &&
      !document.hidden && reason !== 'pagehide';
    window.CgauEditorialEntranceBoot?.release(reason !== 'complete');
    if (activeScene) activeScene.remove();
    scene = null;
    restorePage();
    setState(false, false);
    performance.mark(`cgau-editorial:end:${reason}`);
    if (restoreFocus) {
      const heading = document.querySelector('main h1, #MainContent h1, article h1, h1');
      if (heading) {
        const hadTabindex = heading.hasAttribute('tabindex');
        if (!hadTabindex) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        if (!hadTabindex) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
      }
    }
  };

  const failStart = () => {
    window.CgauEditorialEntranceBoot?.release(true);
    restorePage();
    setState(false, false);
  };

  const start = () => {
    const boot = window.CgauEditorialEntranceBoot;
    const kind = boot?.kind || kindForPath(location.pathname);
    const template = kind ? document.getElementById(`cgau-${kind}-entrance-template`) : null;
    if (!boot || boot.cancelled || motion.matches || document.hidden || document.prerendering || !template || scene) {
      failStart();
      return;
    }

    scene = template.content.firstElementChild?.cloneNode(true) || null;
    if (!scene || !scene.classList.contains('cgau-editorial-entrance')) {
      scene = null;
      failStart();
      return;
    }
    scene.setAttribute('role', 'dialog');
    scene.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add('cgau-editorial-running');
    document.body.append(scene);
    if (getComputedStyle(scene).position !== 'fixed') {
      finish('missing-style');
      return;
    }

    for (const child of document.body.children) {
      if (child === scene || child.tagName === 'SCRIPT' || child.tagName === 'TEMPLATE' || child.tagName === 'STYLE') continue;
      previousInert.set(child, child.inert);
      child.inert = true;
    }

    controller = new AbortController();
    const options = { signal: controller.signal };
    // New product portraits fail open to the collection if their request fails.
    if (KEYNOTE_SCENES.includes(kind)) {
      const portrait = scene.querySelector('img');
      if (portrait?.complete && !portrait.naturalWidth) {
        finish('image-error');
        return;
      }
      portrait?.addEventListener('error', () => finish('image-error'), { ...options, once: true });
      // Click (including a touch-generated click) consumes the gesture before removing
      // the overlay, so it cannot activate a product beneath the presentation.
      scene.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        finish('skip');
      }, options);
    }
    scene.querySelector('[data-editorial-skip]')?.addEventListener('click', () => finish('skip'), options);
    window.addEventListener('keydown', event => {
      if (event.key === 'Escape' || event.key === 'Tab') finish('keyboard');
    }, options);
    window.addEventListener('wheel', () => finish('scroll'), { ...options, passive: true });
    window.addEventListener('touchmove', () => finish('touchmove'), { ...options, passive: true });
    window.addEventListener('scroll', () => finish('scroll'), { ...options, passive: true });
    window.addEventListener('pagehide', () => finish('pagehide'), options);
    window.addEventListener('hashchange', () => finish('hash'), options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) finish('hidden'); }, options);
    const cancelForMotion = event => { if (event.matches) finish('reduced-motion'); };
    if (motion.addEventListener) motion.addEventListener('change', cancelForMotion, options);
    else if (motion.addListener) motion.addListener(cancelForMotion);

    setState(true, true);
    performance.mark(`cgau-editorial:${kind}:mounted`);
    // WebKit can defer its first frame until page reveal; the mounted opaque
    // scene takes over the boot safeguard without exposing the document.
    const guardedScene = GUARDED.includes(kind);
    if (guardedScene) boot.takeover?.();
    timer = window.setTimeout(() => finish('start-timeout'), guardedScene ? 5000 : 600);
    frame = requestAnimationFrame(() => {
      if (!scene) return;
      scene.setAttribute('data-running', '');
      performance.mark(`cgau-editorial:${kind}:start`);
      boot.release(false);
      scene.querySelector('[data-editorial-skip]')?.focus({ preventScroll: true });
      clearTimeout(timer);
      timer = window.setTimeout(() => finish('complete'), KEYNOTE_SCENES.includes(kind) ? 1700 : kind === 'nightlab' ? 1600 : WORLD_SCENES.includes(kind) || kind === 'townsville' ? 1200 : kind === 'reach' ? 5400 : 3200);
    });
  };

  window.CgauEditorialEntrance = {
    finish,
    get running() { return Boolean(scene); }
  };
  if (typeof window.CgauEditorialEntrancePending !== 'boolean') setState(false, false);
  else window.CgauEditorialEntranceRunning = false;
  start();

  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    try { sessionStorage.removeItem(KEY); } catch (_) {}
    finish('back-forward');
  });

  window.addEventListener('pageswap', event => {
    if (!nativeEntranceIntent || !event.viewTransition) return;
    if (event.activation?.entry?.url && event.activation.entry.url !== nativeEntranceIntent) return;
    Promise.resolve(event.viewTransition.ready).catch(() => {});
    event.viewTransition.skipTransition();
  });

  window.addEventListener('click', event => {
    nativeEntranceIntent = '';
    // A new navigation intent supersedes any earlier cancelled navigation.
    if (event.target instanceof Element && event.target.closest('a[href]')) {
      try { sessionStorage.removeItem(KEY); } catch (_) {}
    }
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
      motion.matches || document.hidden || document.prerendering) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || link.closest('form') || link.hasAttribute('download') || link.relList.contains('external') ||
      (link.target && link.target.toLowerCase() !== '_self')) return;

    let target;
    try { target = new URL(link.href, location.href); } catch (_) { return; }
    let kind = target.origin === location.origin ? kindForPath(target.pathname) : '';
    if (kind === 'catalog' && link.matches('[data-cgau-shop-range]') && link.closest('.cgau-story-hero')) kind = 'range';
    if (!kind || target.hash || canonical(target.pathname) === canonical(location.pathname) ||
      (kind === 'cinema' && cinemaPaused())) return;
    // Source gate. Identical to the previous flat check for every existing scene;
    // only townsville relaxes the product-page rule.
    if (blockedSourceFor(kind, location.pathname) || blockedTemplateFor(kind)) return;
    // Only the requested header link and homepage stock CTA trigger these scenes.
    let trigger;
    if (WORLD_SCENES.includes(kind)) {
      if (canonical(location.pathname) !== '/') return;
      if (kind === 'drivein' && !link.closest('#cgau-product-worlds')) return;
      trigger = `home-${kind}`;
    }
    if (kind === 'townsville') trigger = 'about-us';
    if (kind === 'catalog') {
      if (!link.closest('#header-group') ||
        !link.matches('a.menu-list__link, a.menu-drawer__menu-item') ||
        link.textContent.trim().replace(/\s+/g, ' ').toLowerCase() !== 'shop all') return;
      trigger = 'header-shop-all';
    }
    if (kind === 'range') {
      if (canonical(location.pathname) !== '/') return;
      trigger = 'hero-shop-range';
    }
    if (KEYNOTE_SCENES.includes(kind)) {
      if (target.search) return;
      const collectionHandle = target.pathname.split('/').filter(Boolean).pop();
      const homeCategoryCard = canonical(location.pathname) === '/' &&
        link.matches('a.cgau-hwc__card[data-collection]') && link.dataset.collection === collectionHandle;
      const headerCategoryLink = Boolean(link.closest('#header-group')) && link.matches('a.menu-list__link, a.mega-menu__link, a.menu-drawer__menu-item');
      if (!homeCategoryCard && !headerCategoryLink) return;
      trigger = homeCategoryCard ? 'home-keynote' : 'header-keynote';
    }
    if (kind === 'stock') {
      if (canonical(location.pathname) !== '/' || !link.matches('a.cgau-home-deals__all')) return;
      trigger = 'home-stock';
    }
    if (heroTrustLabels[kind]) {
      const label = link.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
      if (canonical(location.pathname) !== '/' || !link.closest('.cgau-story-hero') || label !== heroTrustLabels[kind]) return;
    }

    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        v: 1,
        type: kind,
        trigger,
        path: routes[kind],
        from: location.href,
        to: target.href,
        at: Date.now()
      }));
      if (GUARDED.includes(kind)) nativeEntranceIntent = target.href;
    } catch (_) {
      // Storage may be unavailable. Native navigation continues without an entrance.
    }
  });
})();
